import { useEnvironment } from 'ihunter-pathfinder-components';

const ApiMethod = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    PATCH: 'PATCH',
    HEAD: 'HEAD',
    OPTIONS: 'OPTIONS',
    DELETE: 'DELETE',
};
Object.freeze(ApiMethod);

const ApiMimeType = {
    JSON: 'application/json',
    FORM: 'application/x-www-form-urlencoded',
    TEXT: 'text/plain',
    HTML: 'text/html',
    XML: 'application/xml',
    OCTET: 'application/octet-stream',
    BLOB: 'application/octet-stream',
};
Object.freeze(ApiMimeType);

const HttpStatus = {
    200: 'OK',
    201: 'Created',
    202: 'Accepted',
    204: 'No Content',
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    500: 'Internal Server Error',
    // Add more status codes and their corresponding texts as needed
};

class NetworkError extends Error {
    constructor(status, message, cause) {
        super(message);
        this.name = 'NetworkError';
        this.status = status; // http status code
        this.code = null; // application-specific error code
        this.stack = null; // stack trace
        this.details = null; // additional details
        this.cause = cause; // original error

        // Ensure proper prototype chain (important for extending built-ins)
        Object.setPrototypeOf(this, new.target.prototype);
    }

    toString() {
        let output = `${this.message}`;
        if (this.status) output += ` (HTTP ${this.status})`;
        if (this.code) output += ` [code: ${this.code}]`;
        if (this.details) output += `\nDetails: ${JSON.stringify(this.details, null, 2)}`;
        if (this.cause) output += `\nCaused by: ${this.cause.toString()}`;
        return output;
    }
}

export function useApi(server) {
    const { config } = useEnvironment();

    const baseUrl = () => {
        // Attempt to match with a config provided URL, otherwise presume full server
        return config.value?.url[server] || server || config.value?.url?.api;
    };

    const callApi = async (user, method, endpoint, bodyParams, includeCredentials) => {
        try {
            const params = {
                method: method,
                headers: { Accept: 'application/json' },
            };

            if (includeCredentials) {
                params.credentials = 'include';
            }

            if (user) {
                const firebaseToken = await user.getIdToken();
                params.headers['Authorization'] = `Firebase ${firebaseToken}`;
            }

            if (bodyParams) {
                params.body = JSON.stringify(bodyParams);
            }

            const response = await fetch(`${baseUrl()}${endpoint}`, params);

            if (!response.ok) {
                throw response;
            }
            return response;
        } catch (error) {
            if (error instanceof Response) {
                try {
                    const errorJson = await error.json();
                    // errorInfo.code for Firebase Auth errors, code for application-specific errors (e.g. InviteErrorCode)
                    const code = errorJson.errorInfo?.code || errorJson.code;
                    if (code) {
                        error.code = code;
                    }
                } catch (e) {}
            }
            throw error;
        }
    };

    // Supports bearer token, basic username/password and API key (as header or query parameter) authentication methods
    let auth = {
        type: null, // 'bearer' | 'basic' | 'apikey'
        token: null,
        apiKeyName: null, // for apikey
        apiKeyIn: 'header', // 'header' | 'query'
    };

    const setBearerToken = (token) => {
        auth.type = 'bearer';
        auth.token = token;
    };

    const setBasicAuth = (username, password) => {
        auth.type = 'basic';
        auth.token = typeof btoa !== 'undefined' ? btoa(`${username}:${password}`) : Buffer.from(`${username}:${password}`).toString('base64');
    };

    const setApiKey = (name, value, { inQuery = false } = {}) => {
        auth.type = 'apikey';
        auth.apiKeyName = name;
        auth.token = value;
        auth.apiKeyIn = inQuery ? 'query' : 'header';
    };

    const clearAuth = () => {
        auth = { type: null, token: null, apiKeyName: null, apiKeyIn: 'header' };
    };

    /* General request method
    opts: {
        method: ApiMethod: 'GET'|'POST'|'PUT'|'DELETE'|'PATCH'|'HEAD'|'OPTIONS'
        params: { param: value } // query params
        headers: { header: value } // additional headers
        body: any // request body for POST/PUT
        contentType: 'json'|'form'|'text'|'auto'|mime // content-type of body
        accept: 'json'|'text'|'html'|'xml'|'octet'|'blob'|mime // accept header
        user: ref(FirebaseUser) // Firebase user ref for bearer token
        credentials: 'omit'|'same-origin'|'include' // fetch credentials mode
        etag: string // ETag value for conditional requests (sets If-None-Match header)
    } */
    const request = async (endpoint, opts = {}) => {
        const method = (opts.method || ApiMethod.GET).toUpperCase();
        const headers = opts.headers || {};
        const user = opts.user || null;
        const credentials = opts.credentials === true ? 'include' : opts.credentials || 'omit';

        if (!fetch) {
            throw new NetworkError(500, 'No fetch');
        }

        const acceptHeader = normalizeAccept(opts.accept);
        if (acceptHeader) {
            headers['Accept'] = acceptHeader;
        }

        // Add If-None-Match header for conditional requests with ETags
        if (opts.etag) {
            headers['If-None-Match'] = opts.etag;
        }

        // Prepare URL and query params
        const params = Object.assign({}, opts.params || {});
        if (auth.type === 'apikey' && auth.apiKeyIn === 'query') {
            params[auth.apiKeyName] = auth.token;
        }
        const url = new URL((baseUrl() || '') + endpoint, baseUrl() || undefined);
        Object.entries(params || {}).forEach(([k, v]) => {
            if (v === undefined || v === null) {
                return;
            }
            if (Array.isArray(v)) {
                v.forEach((item) => url.searchParams.append(k, item));
            } else {
                url.searchParams.set(k, String(v));
            }
        });

        // Prepare auth headers
        if (user && user.value) {
            try {
                const firebaseToken = await user.value.getIdToken();
                auth.token = firebaseToken;
                auth.type = 'firebase';
            } catch (error) {
                console.warn('Failed to get Firebase token:', error);
            }
        }
        if (auth.type === 'firebase') {
            headers['Authorization'] = `Firebase ${auth.token}`;
        } else if (auth.type === 'bearer') {
            headers['Authorization'] = `Bearer ${auth.token}`;
        } else if (auth.type === 'basic') {
            headers['Authorization'] = `Basic ${auth.token}`;
        } else if (auth.type === 'apikey' && auth.apiKeyIn === 'header') {
            headers[auth.apiKeyName] = auth.token;
        }

        // Prepare body and Content-Type
        let contentType = normalizeContentType(opts.contentType || 'auto', opts.body);
        if (contentType) {
            headers['Content-Type'] = contentType;
        }

        const body = method === 'GET' || method === 'HEAD' ? undefined : serializeBody(opts.body, contentType);

        try {
            const response = await fetch(url.toString(), {
                method,
                headers,
                credentials,
                body,
            });

            if (!response) {
                return reject(new NetworkError(500, HttpStatus[500]));
            }

            // 304 Not Modified is not an error - return the response
            if (!response.ok && response.status !== 304) {
                throw response;
            }

            return parseResponse(response, opts);
        } catch (error) {
            const err = new NetworkError(error.status || 500);
            if (error instanceof Response) {
                try {
                    let type = error.headers.get('Content-Type');
                    if (type && type.startsWith(ApiMimeType.JSON)) {
                        const errorJson = await error.json();

                        // let functionsPayload = {
                        //     status: 401,                                    // Http status code; recommend deprecate
                        //     error: 'Developer error message',               // Shown in the console or logs; for diagnostics and troubleshooting
                        //     codePrefix: 'auth',                             // Defines a class of errors (ie. 'auth') for handling logic
                        //     errorInfo: {
                        //         code: 'auth/id-token-expired',              // API specific error code; for translation into user message
                        //         message: 'Developer error message'          // Shown in the console or logs; for diagnostics and troubleshooting
                        //     },
                        //     stack: 'stack trace'
                        // }

                        // Developer friendly message for diagnostics and troubleshooting typically shown in the console or logs
                        const errorMessage = errorJson.message || errorJson?.error?.message || errorJson?.errorInfo?.message || '';
                        if (errorMessage && (!err.message || err.message === '')) {
                            err.message = errorMessage;
                        } else if (errorMessage) {
                            err.details = errorMessage;
                        }

                        // Application-specific error code for clientside translation into user message
                        if (errorJson?.code || errorJson?.error?.code || errorJson?.errorInfo?.code) {
                            err.code = errorJson?.code || errorJson?.error?.code || errorJson.errorInfo.code;
                        }
                        // Stack trace
                        if (errorJson.stack || errorJson?.error?.stack || errorJson?.errorInfo?.stack) {
                            err.stack = errorJson?.stack || errorJson?.error?.stack || errorJson?.errorInfo?.stack;
                        }
                    } else {
                        let message = await error.text();
                        if (!err.message || err.message === '') {
                            err.message = message;
                        } else {
                            err.details = message;
                        }
                    }
                } catch (e) {
                    console.error(e);
                }
                throw err;
            } else {
                throw new NetworkError(500, error.message, error);
            }
        }
    };

    // A few helper methods to simplify common request patterns
    const httpGetJson = async (endpoint, opts) => {
        return request(endpoint, { ...opts, method: ApiMethod.GET, accept: 'json' });
    };
    const httpGetXml = async (endpoint, opts) => {
        return request(endpoint, { ...opts, method: ApiMethod.GET, accept: 'xml' });
    };
    const httpPostJson = async (endpoint, body, opts) => {
        return request(endpoint, { ...opts, method: ApiMethod.POST, body, accept: 'json', contentType: 'json' });
    };
    const httpDeleteJson = async (endpoint, opts) => {
        return request(endpoint, { ...opts, method: ApiMethod.DELETE, accept: 'json' });
    };

    const parseResponse = async (response, opts) => {
        const status = response.status;
        const contentType = (response.headers && response.headers.get && response.headers.get('content-type')) || '';
        const accept = opts.accept || null;

        let parsed;
        try {
            if (status === 204 || status === 304) {
                // 204: no content, 304: not modified
                parsed = null;
            } else if (accept === 'blob' || (accept === null && contentType.includes(ApiMimeType.BLOB))) {
                parsed = await response.blob();
            } else if (accept === 'arrayBuffer') {
                parsed = await response.arrayBuffer();
            } else if (accept === 'formData') {
                parsed = await response.formData();
            } else if (accept === 'text' || (accept === null && (contentType.startsWith('text/') || contentType === ''))) {
                parsed = await response.text();
            } else {
                // default: try JSON if content-type indicates JSON
                if (contentType.includes(ApiMimeType.JSON) || accept === 'json') {
                    parsed = await response.json();
                } else {
                    parsed = await response.text(); // fallback to text
                }
            }
        } catch (parseErr) {
            parsed = null;
        }

        // If includeResponse is requested, return both data and response metadata
        if (opts.includeResponse) {
            return {
                data: parsed,
                status: response.status,
                headers: response.headers,
                ok: response.ok,
            };
        }

        return parsed;
    };

    const normalizeAccept = (accept) => {
        // accept: 'json'|'text'|'html'|'xml'|'octet'|'blob' or any mime
        if (!accept) {
            return undefined;
        }
        const map = {
            json: ApiMimeType.JSON,
            text: ApiMimeType.TEXT,
            html: ApiMimeType.HTML,
            xml: ApiMimeType.XML,
            octet: ApiMimeType.OCTET,
            form: ApiMimeType.FORM,
        };

        return map[accept] || accept;
    };

    const normalizeContentType = (contentType, body) => {
        // contentType: 'json'|'form'|'multipart'|'text'|'auto'|mime
        if (!contentType || contentType === 'auto') {
            if (body instanceof FormData) {
                return null; // browser sets boundary
            } else if (body instanceof URLSearchParams) {
                return ApiMimeType.FORM;
            } else if (body instanceof Blob) {
                return body.type || ApiMimeType.BLOB;
            } else if (typeof body === 'string') {
                return ApiMimeType.TEXT;
            } else if (typeof body === 'object') {
                return ApiMimeType.JSON;
            }
            return null;
        }
        const map = {
            json: ApiMimeType.JSON,
            form: ApiMimeType.FORM,
            text: ApiMimeType.TEXT,
            multipart: null, // don't set; browser will set with boundary when using FormData
        };
        return map[contentType] ?? contentType;
    };

    const serializeBody = (body, contentType) => {
        // Convert JS-type body to a proper payload
        if (body === undefined || body === null) {
            return undefined;
        }
        if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer || body instanceof URLSearchParams) {
            return body;
        }
        if (contentType && contentType.includes(ApiMimeType.JSON)) {
            return JSON.stringify(body);
        }
        if (contentType && contentType.includes(ApiMimeType.FORM)) {
            if (typeof body === 'string') {
                return body;
            }
            const usp = new URLSearchParams();
            Object.entries(body).forEach(([k, v]) => {
                if (v === undefined || v === null) {
                    return;
                }
                if (Array.isArray(v)) {
                    v.forEach((i) => usp.append(k, i));
                } else {
                    usp.set(k, String(v));
                }
            });
            return usp.toString();
        }
        // fallback: if string, return; if object, JSON.stringify
        if (typeof body === 'string') {
            return body;
        }
        if (typeof body === 'object') {
            return JSON.stringify(body);
        }
        return body;
    };

    return {
        callApi,

        request,

        httpGetJson,
        httpGetXml,
        httpPostJson,
        httpDeleteJson,

        setBearerToken,
        setBasicAuth,
        setApiKey,
        clearAuth,

        NetworkError,
        ApiMethod,
        ApiMimeType,
    };
}
